import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle, 
  AlertTriangle,
  FileText,
  Volume2,
  Image as ImageIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

interface Question {
  id: string;
  order: number;
  questionType: 'MCQ' | 'TYPE_IN';
  questionText: string;
  choiceA?: string;
  choiceB?: string;
  choiceC?: string;
  choiceD?: string;
  correctAnswer: string;
  explanation?: string;
  mediaFile?: string;
}

interface Quiz {
  id: string;
  title: string;
  subject: string;
  description?: string;
  timeLimit?: number;
  questions: Question[];
  creator?: { name: string };
}

interface QuizAttempt {
  id: string;
  startTime: string;
  quiz: Quiz;
}

// Helper functions for media file handling
const isImageFile = (filename: string | undefined): boolean => {
  if (!filename) return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
};

const isAudioFile = (filename: string | undefined): boolean => {
  if (!filename) return false;
  const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];
  return audioExtensions.some(ext => filename.toLowerCase().endsWith(ext));
};

const getMediaUrl = (mediaFilePath: string | undefined): string => {
  if (!mediaFilePath) return '';
  // Extract just the filename from the path
  const filename = mediaFilePath.split('/').pop() || mediaFilePath;
  return `/api/quizzes/media/${filename}`;
};

const QuizPlayerPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  useEffect(() => {
    if (quizId) {
      startQuiz();
    }
  }, [quizId]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (attempt && attempt.quiz.timeLimit && timeRemaining !== null && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev === null || prev <= 1) {
            // Auto-submit when time runs out
            handleSubmitQuiz(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [attempt, timeRemaining]);

  const startQuiz = async () => {
    if (!quizId || user?.role !== 'STUDENT') {
      navigate('/quizzes');
      return;
    }

    try {
      setError('');
      const response = await api.post(`/quizzes/${quizId}/start`);
      const attemptData = response.data;
      
      setAttempt(attemptData);
      setQuiz(attemptData.quiz);
      
      // Initialize timer if timed quiz
      if (attemptData.quiz.timeLimit) {
        setTimeRemaining(attemptData.quiz.timeLimit);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to start quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = async (questionId: string, answer: string) => {
    if (!attempt) return;

    setAnswers(prev => ({ ...prev, [questionId]: answer }));

    // Save answer to backend
    try {
      await api.post(`/quizzes/attempts/${attempt.id}/answer`, {
        questionId,
        answer
      });
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  const handleSubmitQuiz = async (autoSubmit = false) => {
    if (!attempt || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await api.post(`/quizzes/attempts/${attempt.id}/submit`);
      navigate(`/quiz-results/${attempt.id}`);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to submit quiz');
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (!timeRemaining || !quiz?.timeLimit) return 'text-gray-600';
    const percentage = (timeRemaining / quiz.timeLimit) * 100;
    if (percentage <= 10) return 'text-red-600';
    if (percentage <= 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  const currentQuestion = quiz?.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === (quiz?.questions.length || 0) - 1;
  const answeredQuestions = Object.keys(answers).length;
  const totalQuestions = quiz?.questions.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Starting quiz...</p>
        </div>
      </div>
    );
  }

  if (error && !quiz) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate('/quizzes')} className="btn btn-primary">
            Back to Quizzes
          </button>
        </div>
      </div>
    );
  }

  if (!quiz || !currentQuestion) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{quiz.title}</h1>
              <p className="text-sm text-gray-600">by {quiz.creator?.name || 'Unknown'}</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </div>
              
              {quiz.timeLimit && timeRemaining !== null && (
                <div className={`flex items-center text-sm font-medium ${getTimeColor()}`}>
                  <Clock className="w-4 h-4 mr-1" />
                  {formatTime(timeRemaining)}
                </div>
              )}
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Question */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 flex-1">
                {currentQuestion.questionText}
              </h2>
              
              <span className="ml-4 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                {currentQuestion.questionType}
              </span>
            </div>
            
            {/* Media */}
            {currentQuestion.mediaFile && (
              <div className="mb-6 p-4 border rounded-lg">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  {isImageFile(currentQuestion.mediaFile) ? (
                    <ImageIcon className="w-4 h-4 mr-2" />
                  ) : isAudioFile(currentQuestion.mediaFile) ? (
                    <Volume2 className="w-4 h-4 mr-2" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  Media file attached
                </div>
                
                {isImageFile(currentQuestion.mediaFile) ? (
                  <img 
                    src={getMediaUrl(currentQuestion.mediaFile)}
                    alt="Question media"
                    className="max-w-full h-auto rounded"
                    onError={(e) => {
                      console.error('Failed to load image:', getMediaUrl(currentQuestion.mediaFile));
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : isAudioFile(currentQuestion.mediaFile) ? (
                  <audio controls className="w-full">
                    <source src={getMediaUrl(currentQuestion.mediaFile)} />
                    Your browser does not support the audio element.
                  </audio>
                ) : (
                  <a 
                    href={getMediaUrl(currentQuestion.mediaFile)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    View attached file
                  </a>
                )}
              </div>
            )}
          </div>
          
          {/* Answer Options */}
          <div className="mb-8">
            {currentQuestion.questionType === 'MCQ' ? (
              <div className="space-y-3">
                {['A', 'B', 'C', 'D'].map(letter => {
                  const choice = currentQuestion[`choice${letter}` as keyof Question] as string;
                  if (!choice) return null;
                  
                  return (
                    <label
                      key={letter}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        answers[currentQuestion.id] === choice
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion.id}`}
                        value={choice}
                        checked={answers[currentQuestion.id] === choice}
                        onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                        className="mr-4"
                      />
                      
                      <span className="text-gray-900">
                        <span className="font-medium">{letter}.</span> {choice}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Answer:
                </label>
                <input
                  type="text"
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  className="input w-full text-lg"
                  placeholder="Type your answer here..."
                  autoFocus
                />
              </div>
            )}
          </div>
          
          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
              className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </button>
            
            <div className="text-sm text-gray-600">
              {answeredQuestions} of {totalQuestions} answered
            </div>
            
            {isLastQuestion ? (
              <button
                onClick={() => setShowConfirmSubmit(true)}
                className="btn btn-primary"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Quiz
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestionIndex(prev => 
                  Math.min(totalQuestions - 1, prev + 1)
                )}
                className="btn btn-primary"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            )}
          </div>
        </div>
        
        {/* Question Navigation */}
        <div className="mt-6 bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Question Navigation</h3>
          <div className="grid grid-cols-10 gap-2">
            {quiz.questions.map((question, index) => (
              <button
                key={question.id}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                  index === currentQuestionIndex
                    ? 'bg-blue-600 text-white'
                    : answers[question.id]
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Submit Confirmation Modal */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Quiz?</h3>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">
                You have answered {answeredQuestions} out of {totalQuestions} questions.
              </p>
              
              {answeredQuestions < totalQuestions && (
                <p className="text-yellow-600 text-sm">
                  ⚠️ You have {totalQuestions - answeredQuestions} unanswered question(s).
                </p>
              )}
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                {error}
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmSubmit(false)}
                className="btn btn-outline flex-1"
                disabled={submitting}
              >
                Continue Quiz
              </button>
              
              <button
                onClick={() => handleSubmitQuiz()}
                className="btn btn-primary flex-1"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Quiz'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizPlayerPage;